import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Modal, Space, Button, Spin, message, Tooltip } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import { renderIntlLabel } from '@/utils';
import * as PayApi from '@/api/payment';
import { parse2Number, parseFen2Currency, parseYuan2Fen } from '@/utils/number';

import { PayResult, BusinessType, PayModalProps, OpenPayModalProps, PayWay } from './interface';
import PayOrderConfirmModal from '../PaymentOrderModal/PayOrderConfirmModal';
import PaymentOrderModal from '../PaymentOrderModal';
import Barcode from './barcode';
import Cash from './cash';
import Bank from './Bank';
import VipCard from './vipCard';
import XuanZhu from './xuanzhu';
import { VipAlertText } from './alert';
import styles from './payModal.module.scss';

const PayModal: React.FC<PayModalProps> = forwardRef((props, ref) => {
  // 会员信息
  const defaultVipInfo = {
    accountType: 0, // 会员账户类型
    balance: -1, // 账户余额
    wyuid: '', // 身份标识
    isWyUser: false, // 是否网鱼电酒用户
    isVip: false, // 是否会员
  };

  // 选住会员信息
  const defaultXuanzhuVipInfo = {
    balance: -1, // 账户余额
    wyuid: '', // 身份标识
  };

  // 会员充值
  const defaultRechargeInfo = {
    accountType: 0, // 会员类型
    wyuid: '', // 网鱼id
    rechargeAmount: 0, // 充值金额
    rechargeGiving: 0, // 充值赠送金
  };

  // 付款单
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [paymentOrderOpen, setPaymentOrderOpen] = useState<boolean>(false);

  // self states
  const [modalVisible, setModalVisible] = useState(false); // 收款窗口可见状态
  const [rafId, setRafId] = useState(0); // requestAnimationFrame id
  const [payType, setPayType] = useState(3); // 支付方式
  const [payAmount, setPayAmount] = useState(0); // 支付金额
  const [payOrderType, setPayOrderType] = useState(0); // 支付单类型
  const [paymentNo, setPaymentNo] = useState(''); // 支付单号

  const [businessType, setBusinessType] = useState<BusinessType>(1); // 收款业务类型
  const [certNo, setCertNo] = useState(''); // 身份证号
  const [wyuid, setWyuid] = useState('');
  const [rechargeInfo, setRechargeInfo] = useState(defaultRechargeInfo); // 会员充值

  // 同步子组件数据
  const [inputCash, setInputCash] = useState(0); // 输入的现金(关联现金支付子组件)
  const [vipInfo, setVipInfo] = useState(defaultVipInfo); // 会员卡信息(关联会员卡扣子组件)
  const [xuanzhuVipInfo, setXuanzhuVipInfo] = useState(defaultXuanzhuVipInfo); // 选住会员卡信息(关联选住余额支付子组件)

  // loading states
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const extraParamsRef = useRef<Object>();

  // init
  const openModal = (params: OpenPayModalProps) => {
    extraParamsRef.current = params.extraParams || {};
    // 付款单二次确认
    if (params.showFukuanDanConfirm) {
      setConfirmOpen(true);
      return;
    }
    // 打开付款单
    if (params.payType === PayWay.Fukuandan) {
      setPaymentNo(params.paymentNo);
      setPaymentOrderOpen(true);
      return;
    }
    setPayType(params.payType);
    setPaymentNo(params.paymentNo);
    // 金额特殊处理，浮点数向上取整
    const formatPayAmount = Math.abs(Math.ceil(params.payAmount));
    setPayAmount(formatPayAmount);
    setPayOrderType(params.payOrderType);

    setBusinessType(params.businessType || 1); // 收款业务类型, 默认1: 房间消费
    setCertNo(params.certNo || '');
    setWyuid(params.wyuid);
    if (params.businessType === 3) {
      setRechargeInfo({
        accountType: params.accountType || 0,
        wyuid: params.wyuid || '',
        rechargeAmount: params.rechargeAmount || 0,
        rechargeGiving: params.rechargeGiving || 0,
      });
    }
    // 其他收款方式
    setModalVisible(true);
  };

  const handleConfirmOk = () => {
    setConfirmOpen(false);
    if (props.onConfirmOk) {
      props.onConfirmOk();
    }
  };

  const handleConfirmCancel = () => {
    setConfirmOpen(false);
    if (props.onConfirmCancel) {
      props.onConfirmCancel();
    }
  };

  const onGenerateSuccess = () => {
    props.payResultCallback({ generatePayOrder: true });
  };

  const handleGenerateOrderClose = () => {
    setPaymentOrderOpen(false);
    props.payResultCallback({ closePayOrderModal: true });
  };

  useImperativeHandle(ref, () => ({
    openModal,
  }));

  let start;
  let retryCount = 0;
  const retryLimit = 40;
  // poll request transaction result per 3 seconds
  // if error, retry request, until success
  const fetchTransResult = async (timestamp) => {
    if (start === undefined) {
      start = timestamp;
    }

    const elapsed = timestamp - start;

    // 1000ms gap
    if (elapsed > 1000) {
      // setTransactionLoading(true);
      try {
        const res = await PayApi.fetchTransactionResult({
          orderNo: paymentNo,
        });

        const payResult: PayResult = {
          payed: true,
          result: false,
        };

        // if retryCount lt retryLimit, retry
        const checkRetryMax = () => {
          if (retryCount <= retryLimit) {
            // delay 2000ms, retry
            setTimeout(() => {
              retryCount += 1;
              start = undefined;
              setRafId(requestAnimationFrame(fetchTransResult));
            }, 2000);
          } else {
            setTransactionLoading(false);
            setModalVisible(false);
            message.error(renderIntlLabel('bill_msg_receive_money_unsuccessfully'));

            // clear raf
            if (rafId) cancelAnimationFrame(rafId);
            props.payResultCallback({ ...payResult, result: false });
          }
        };

        if (res && res.status) {
          // success
          if (res.data.businessStatus && res.data.paymentStatus) {
            // 线下银行卡
            if (payType === 18) {
              message.success(renderIntlLabel('bill_msg_receive_money_successfully'));
            }
            setTransactionLoading(false);
            setModalVisible(false);

            // reset variables
            retryCount = 0;
            start = undefined;

            // callback payResult
            props.payResultCallback({ ...payResult, result: true });

            // clear raf
            if (rafId) cancelAnimationFrame(rafId);
          } else {
            checkRetryMax();
          }
        } else {
          checkRetryMax();
        }
      } catch (err) {
        setTransactionLoading(false);
        // clear raf
        if (rafId) cancelAnimationFrame(rafId);
      }
    } else {
      setRafId(requestAnimationFrame(fetchTransResult));
    }
  };

  // 根据支付方式生成支付参数
  const generatePayParams = (type, code, info) => {
    const defaultPayParams = {
      ...extraParamsRef.current,
      orderNo: paymentNo, // 支付单号
      orderType: payOrderType || 102, // 支付单类型
      payAmount, // 付款金额
      payWay: payType, // 支付方式
      payDetail: {
        bizType: businessType,
        paySubjects: [
          {
            cash: payAmount, // 支付现金
          },
        ],
      },
    };
    // 现金、线下银行卡
    let payParams: any = defaultPayParams;

    // 扫码
    if (type === 3) {
      payParams = {
        ...defaultPayParams,
        payDetail: {
          ...defaultPayParams.payDetail,
          scanCode: code, // 付款码
        },
      };
    }

    // 会员卡扣
    if (type === 2) {
      payParams = {
        ...defaultPayParams,
        accountType: info.accountType,
        payDetail: {
          ...defaultPayParams.payDetail,
          amount: payAmount, // 会员卡扣款金额
          businessType: '2001', // 业务类型，暂时写死
          isPass: 0, // 是否免密支付，暂时默认免密
          onlineCash: 0, // 用户在线消费金额，暂时写死
          // password: '', // 密码，暂无
          paySubjects: [
            {
              cash: payAmount, // 支付现金
              payType: 2, // 付款科目类型，暂时写死
            },
          ],
          wyuid: info.wyuid,
        },
      };
    }

    // 选住余额
    if (type === 17) {
      payParams = {
        ...defaultPayParams,
        payDetail: {
          ...defaultPayParams.payDetail,
          wyuid: xuanzhuVipInfo.wyuid,
        },
      };
    }

    return payParams;
  };

  // 收款
  const payRequest = async (type, code, info) => {
    setTransactionLoading(true);
    try {
      const params = generatePayParams(type, code, info);

      const res = await PayApi.pay(params);

      if (res && res.status) {
        setRafId(requestAnimationFrame(fetchTransResult));
      } else {
        message.error(res.msg || renderIntlLabel('bill_msg_pay_unsuccessfully'));
        setTransactionLoading(false);
      }
    } catch (err) {
      setTransactionLoading(false);
    }
  };

  // 会员充值
  const rechargeRequest = async (type, code) => {
    setTransactionLoading(true);
    try {
      const rechargeParams = {
        orderNo: paymentNo, // 支付单号
        orderType: payOrderType || 101, // 支付单类型
        payAmount, // 付款金额
        payWay: payType, // 支付方式
        accountType: rechargeInfo.accountType,
        rechargeCash: rechargeInfo.rechargeAmount
          ? Math.abs(rechargeInfo.rechargeAmount)
          : payAmount,
        rechargeGiving: rechargeInfo.rechargeGiving || 0,
        payDetail: {
          paySubjects: [
            {
              cash: payAmount, // 支付现金
            },
          ],
          wyuid: rechargeInfo.wyuid,
          scanCode: code, // 付款码
        },
        wyuid: rechargeInfo.wyuid,
      };

      // 现金
      if (type === 1) delete rechargeParams.payDetail.scanCode;

      const res = await PayApi.recharge(rechargeParams);

      if (res && res.status) {
        setRafId(requestAnimationFrame(fetchTransResult));
      } else {
        message.error(res.msg || renderIntlLabel('bill_msg_member_recharge_unsuccessfully'));
        setTransactionLoading(false);
      }
    } catch (err) {
      setTransactionLoading(false);
    }
  };

  // 支付提交
  const payEntry = async (type, code?, info?) => {
    if (businessType === 3) {
      await rechargeRequest(type, code);
    } else {
      await payRequest(type, code, info);
    }
  };

  // 扫码支付
  const submitBarcodePay = async (code) => {
    try {
      setBarcodeLoading(true);
      payEntry(payType, code);
      setBarcodeLoading(false);
    } catch (err) {
      setBarcodeLoading(false);
    }
  };

  // 现金支付提交
  const submitCashPay = async () => {
    // check inputed cash
    if (parse2Number(parseYuan2Fen(inputCash)) < payAmount) {
      return;
    }

    payEntry(payType);
  };

  const submitBankPay = () => {
    payEntry(payType);
  };

  // 选住余额支付提交
  const submitXuanZhuPay = async () => {
    payEntry(payType);
  };

  // 会员卡支付提交
  const submitVipCardPay = async (info) => {
    payEntry(payType, null, info);
  };

  // reset states
  const resetCurrentStates = () => {
    start = undefined;
    retryCount = 0;

    setModalVisible(false);
    setRafId(0);
    setPayType(3);
    setPayAmount(0);
    setPayOrderType(0);
    setPaymentNo('');

    setBusinessType(1);
    setCertNo('');
    setWyuid('');
    setRechargeInfo(defaultRechargeInfo);

    setInputCash(0);
    setVipInfo(defaultVipInfo);
    setXuanzhuVipInfo(defaultXuanzhuVipInfo);

    setConfirmOpen(false);
    setPaymentOrderOpen(false);
  };

  // 关闭收银台
  const closeModal = () => {
    // clear raf
    if (rafId) cancelAnimationFrame(rafId);

    resetCurrentStates();

    // callback payResult
    props.payResultCallback({
      payed: false,
      result: false,
    });
  };

  // 会员卡支付收款按钮可点击状态
  const disabledVipCardSubmit = () => {
    return !vipInfo.isWyUser || !vipInfo.isVip || vipInfo.balance < payAmount;
  };

  // 现金支付收款按钮可点击状态
  const disabledCashSubmit = () => {
    return payAmount > parse2Number(parseYuan2Fen(inputCash));
  };

  // 选住余额收款按钮可点击状态
  const disabledXuanZhuSubmit = () => {
    return xuanzhuVipInfo.balance < payAmount;
  };

  // reset states & clear raf when unmounted
  useEffect(() => {
    return () => {
      // clear raf
      if (rafId) cancelAnimationFrame(rafId);

      resetCurrentStates();
    };
  }, []);

  // render footer actions
  const modalFooter = () => {
    // 扫码
    if (payType === 3) return null;

    // 现金
    if (payType === 1) {
      return (
        <Space align="start" size={16}>
          <Button onClick={() => closeModal()}>{renderIntlLabel('order_action_cancel')}</Button>
          <Button
            type="primary"
            disabled={disabledCashSubmit()}
            loading={transactionLoading}
            onClick={() => submitCashPay()}
          >
            {renderIntlLabel('bill_action_confirm_receive_money')}
          </Button>
        </Space>
      );
    }

    // 线下银行卡
    if (payType === 18) {
      return (
        <Space align="start" size={16}>
          <Button onClick={() => closeModal()}>{renderIntlLabel('order_action_cancel')}</Button>
          <Button type="primary" loading={transactionLoading} onClick={() => submitBankPay()}>
            {renderIntlLabel('bill_action_confirm_receive_money')}
          </Button>
        </Space>
      );
    }

    // 会员卡
    if (payType === 2) {
      return (
        <Space align="start" size={16}>
          <Button onClick={() => closeModal()}>{renderIntlLabel('order_action_cancel')}</Button>
          {disabledVipCardSubmit() ? (
            <Tooltip placement="top" title={renderIntlLabel('bill_msg_balance_insufficient')}>
              <Button type="primary" disabled>
                {renderIntlLabel('bill_action_confirm_receive_money')}
              </Button>
            </Tooltip>
          ) : (
            <Button
              type="primary"
              disabled={disabledVipCardSubmit()}
              loading={transactionLoading}
              onClick={() => submitVipCardPay(vipInfo)}
            >
              {renderIntlLabel('bill_action_confirm_receive_money')}
            </Button>
          )}
        </Space>
      );
    }

    // 选住余额
    if (payType === 17) {
      return (
        <Space align="start" size={16}>
          <Button onClick={() => closeModal()}>{renderIntlLabel('order_action_cancel')}</Button>
          <Button
            type="primary"
            disabled={disabledXuanZhuSubmit()}
            loading={transactionLoading}
            onClick={() => submitXuanZhuPay()}
          >
            {renderIntlLabel('bill_action_confirm_receive_money')}
          </Button>
        </Space>
      );
    }

    return null;
  };

  // render title
  const renderTitle = () => {
    if (payType === 3) return renderIntlLabel('bill_enum_payWay_scan');

    if (payType === 1) return renderIntlLabel('bill_enum_payWay_receive_by_cash');

    if (payType === 2) return renderIntlLabel('bill_enum_payWay_stored_remaining_balance');

    if (payType === 17) return renderIntlLabel('bill_enum_payWay_xuanzhu_remaining_balance');

    return null;
  };

  // render pay content by payType
  const renderPayContent = () => {
    // 扫码
    if (payType === 3) {
      return (
        <Barcode
          payModalVisible={modalVisible}
          barcodeLoading={barcodeLoading}
          submitBarcodePay={(barcode) => submitBarcodePay(barcode)}
        />
      );
    }

    // 现金
    if (payType === 1) {
      return (
        <Cash
          payAmount={payAmount}
          payModalVisible={modalVisible}
          inputCashCallback={({ isValid, cash }) => {
            if (!isValid) {
              setInputCash(0);
            } else {
              setInputCash(cash);
            }
          }}
          submitCashPay={() => submitCashPay()}
        />
      );
    }

    // 会员卡扣
    if (payType === 2) {
      return (
        <VipCard
          certNo={certNo}
          wyuid={wyuid}
          payAmount={payAmount}
          payModalVisible={modalVisible}
          businessType={businessType}
          vipInfoChangeCallback={(info) => setVipInfo(info)}
          submitVipCardPay={(info) => submitVipCardPay(info)}
        />
      );
    }

    // 选住余额
    if (payType === 17) {
      return (
        <XuanZhu
          certNo={certNo}
          wyuid={wyuid}
          payAmount={payAmount}
          payModalVisible={modalVisible}
          balanceChangeCallback={(info) => setXuanzhuVipInfo(info)}
          submitXuanZhuPay={() => submitXuanZhuPay()}
        />
      );
    }

    return null;
  };

  return (
    <>
      <PayOrderConfirmModal
        open={confirmOpen}
        onOk={handleConfirmOk}
        onCancel={handleConfirmCancel}
      />
      <PaymentOrderModal
        orderNo={paymentNo}
        open={paymentOrderOpen}
        onGenerateSuccess={onGenerateSuccess}
        onClose={handleGenerateOrderClose}
      />

      <Modal
        width={560}
        title={renderIntlLabel('bill_title_receive_money')}
        visible={modalVisible}
        maskClosable={false}
        keyboard={false}
        footer={modalFooter()}
        destroyOnClose
        onCancel={() => closeModal()}
      >
        {payType === 2 ? <VipAlertText payAmount={payAmount} vipInfo={vipInfo} /> : null}
        <Spin
          style={{ width: '100%' }}
          spinning={transactionLoading}
          tip={renderIntlLabel('bill_tips_wait_for_pay')}
          indicator={<LoadingOutlined style={{ fontSize: 14 }} spin />}
        >
          <div className={styles['pay-wrapper']}>
            {/* 线下银行卡 */}
            {payType === 18 ? (
              <Bank payAmount={payAmount} />
            ) : (
              <>
                <h4>{renderTitle()}</h4>
                <div className={styles.payAmountText}>{parseFen2Currency(payAmount)}</div>
                {renderPayContent()}
              </>
            )}
          </div>
        </Spin>
      </Modal>
    </>
  );
});

export default PayModal;
